import { Clipboard } from '@angular/cdk/clipboard';
import {
  Component,
  Input,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';

/**
 * Shows the SF Customer Number of the current account, read-only.
 * A click on the number copies it to the clipboard. The tooltip of the number then shows
 * "Copied" for a short time.
 */
@Component({
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  selector: 'app-customer-number',
  templateUrl: './customer-number.component.html',
  styleUrls: ['./customer-number.component.scss'],
})
export class CustomerNumberComponent implements OnDestroy {
  public static readonly PLACEHOLDER = '-';
  public static readonly COPIED_DISPLAY_MS = 2000;

  /** Shows a copy icon in front of the text, as a user menu entry does. */
  @Input() public withIcon = false;

  public readonly customerNumber$: Observable<string>;
  public copied = false;

  private _copiedTimer: ReturnType<typeof setTimeout> | undefined;

  public constructor(
    companyService: CompanyService,
    private readonly _clipboard: Clipboard
  ) {
    this.customerNumber$ = companyService.company$.pipe(
      map(
        (company) =>
          company?.customerNumber || CustomerNumberComponent.PLACEHOLDER
      ),
      catchError(() => of(CustomerNumberComponent.PLACEHOLDER))
    );
  }

  public ngOnDestroy(): void {
    clearTimeout(this._copiedTimer);
  }

  public async copy(): Promise<void> {
    const customerNumber = await firstValueFrom(this.customerNumber$);
    if (
      customerNumber === CustomerNumberComponent.PLACEHOLDER ||
      !this._clipboard.copy(customerNumber)
    ) {
      return;
    }

    this.copied = true;
    clearTimeout(this._copiedTimer);
    this._copiedTimer = setTimeout(
      () => (this.copied = false),
      CustomerNumberComponent.COPIED_DISPLAY_MS
    );
  }
}
